import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AIProposalAnalysis.module.css';
import Footer from '../../../components/common/Footer/Footer';

const AIProposalAnalysis = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(null);

  const handleGetStarted = () => {
    const isAuthenticated = localStorage.getItem('token');
    if (isAuthenticated) {
      navigate('/homeowner/dashboard');
    } else {
      navigate('/homeowner/login');
    }
  };

  const handleCreateComparison = () => {
    const isAuthenticated = localStorage.getItem('token');
    if (isAuthenticated) {
      navigate('/homeowner/create-comparison');
    } else {
      navigate('/homeowner/login');
    }
  };

  const features = [
    {
      id: 'extraction',
      icon: 'fas fa-search-dollar',
      title: 'Smart Data Extraction',
      description: 'Our AI automatically extracts and categorizes key information from proposals including costs, timelines, materials, and labor details.',
      benefits: [
        'Automated cost breakdown analysis',
        'Timeline and milestone extraction',
        'Material specification identification',
        'Labor cost and hour calculations'
      ]
    },
    {
      id: 'compliance',
      icon: 'fas fa-clipboard-check',
      title: 'Compliance & Risk Assessment',
      description: 'Advanced AI algorithms extract and analyze licensing, insurance, permits, and regulatory information while identifying potential project risks.',
      benefits: [
        'License and certification extraction',
        'Insurance coverage analysis',
        'Permit requirement identification',
        'Risk factor assessment and scoring'
      ]
    },
    {
      id: 'scoring',
      icon: 'fas fa-chart-bar',
      title: 'Proposal Evaluation & Ranking',
      description: 'Comprehensive proposal evaluation and contractor ranking based on completeness, quality, and risk assessment.',
      benefits: [
        'Proposal completeness scoring',
        'Quality dimension analysis',
        'Risk-based evaluation',
        'Contractor ranking generation'
      ]
    },
    {
      id: 'insights',
      icon: 'fas fa-lightbulb',
      title: 'Clarification Question Generation',
      description: 'AI generates targeted questions to address gaps and ambiguities in contractor proposals for better decision-making.',
      benefits: [
        'Gap-based question generation',
        'Personalized inquiry suggestions',
        'Professional question formatting',
        'Missing information identification'
      ]
    }
  ];

  const analysisSteps = [
    {
      step: '01',
      title: 'Document Processing',
      description: 'Our AI reads and processes your uploaded proposals using advanced OCR and natural language processing.',
      icon: 'fas fa-file-contract'
    },
    {
      step: '02',
      title: 'Data Extraction',
      description: 'Key information is automatically extracted including costs, timelines, materials, and contractor details.',
      icon: 'fas fa-database'
    },
    {
      step: '03',
      title: 'Analysis & Scoring',
      description: 'Advanced algorithms analyze and score each proposal across multiple criteria and risk factors.',
      icon: 'fas fa-calculator'
    },
    {
      step: '04',
      title: 'Insights & Reports',
      description: 'Receive detailed analysis reports with recommendations, red flags, and actionable insights.',
      icon: 'fas fa-chart-pie'
    }
  ];

  const analysisTypes = [
    {
      title: 'Cost Analysis',
      description: 'Comprehensive pricing structure analysis and cost breakdown extraction across all proposals.',
      icon: 'fas fa-dollar-sign',
      features: ['Cost structure extraction', 'Payment terms analysis', 'Allowance identification', 'Cost transparency assessment']
    },
    {
      title: 'Timeline Analysis',
      description: 'Evaluate project schedules, milestone feasibility, and potential timeline risks.',
      icon: 'fas fa-clock',
      features: ['Timeline information extraction', 'Schedule clarity assessment', 'Milestone identification', 'Timeline completeness evaluation']
    },
    {
      title: 'Specification & Documentation Analysis',
      description: 'Extract material specifications, warranty details, and evaluate proposal documentation quality and scope coverage.',
      icon: 'fas fa-award',
      features: ['Material specification extraction', 'Warranty terms analysis', 'Proposal documentation quality', 'Scope coverage assessment']
    },
    {
      title: 'Risk Evaluation',
      description: 'Identify potential risks, compliance gaps, and concerning patterns in contractor proposals.',
      icon: 'fas fa-exclamation-triangle',
      features: ['Risk factor identification', 'Compliance checking', 'Concerning pattern detection', 'Risk assessment scoring']
    }
  ];



  return (
    <div className={styles.pageContainer}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <div className={styles.heroShapes}>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
          </div>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1>AI Proposal Analysis</h1>
            <p className={styles.heroSubtitle}>
              Harness the power of artificial intelligence to decode, analyze, and evaluate contractor proposals
            </p>
            <p className={styles.heroDescription}>
                    Our advanced AI technology reads through complex proposal documents, extracts key information, 
      identifies risks, and provides comprehensive analysis to support your decision-making.
            </p>
            <div className={styles.heroButtons}>
              <button className={styles.primaryButton} onClick={handleCreateComparison}>
                <i className="fas fa-brain"></i>
                Start AI Analysis
              </button>
              <button className={styles.secondaryButton} onClick={handleGetStarted}>
                <i className="fas fa-chart-line"></i>
                View Dashboard
              </button>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.aiPreview}>
              <div className={styles.previewHeader}>
                <div className={styles.aiStatus}>
                  <div className={styles.statusDot}></div>
                  <span>AI Analysis in Progress</span>
                </div>
              </div>
              <div className={styles.previewContent}>
                <div className={styles.analysisProgress}>
                  <div className={styles.progressItem}>
                    <span>Cost Extraction</span>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div className={styles.progressItem}>
                    <span>Risk Assessment</span>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: '85%' }}></div>
                    </div>
                  </div>
                  <div className={styles.progressItem}>
                    <span>Compliance Check</span>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: '92%' }}></div>
                    </div>
                  </div>
                </div>
                <div className={styles.insightPreview}>
                  <div className={styles.insight}>
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>2 potential risks detected</span>
                  </div>
                  <div className={styles.insight}>
                    <i className="fas fa-search"></i>
                    <span>License information extracted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How AI Analysis Works */}
      <section className={styles.processSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>How AI Analysis Works</h2>
            <p>Advanced artificial intelligence technology that transforms how you evaluate proposals</p>
          </div>
          <div className={styles.processGrid}>
            {analysisSteps.map((step, index) => (
              <div key={index} className={styles.processStep}>
                <div className={styles.stepNumber}>{step.step}</div>
                <div className={styles.stepIcon}>
                  <i className={step.icon}></i>
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                {index < analysisSteps.length - 1 && (
                  <div className={styles.stepConnector}>
                    <i className="fas fa-arrow-right"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Intelligent Analysis Features</h2>
            <p>Comprehensive AI-powered tools to evaluate every aspect of contractor proposals</p>
          </div>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div 
                key={feature.id} 
                className={`${styles.featureCard} ${activeFeature === feature.id ? styles.active : ''}`}
                onMouseEnter={() => setActiveFeature(feature.id)}
                onMouseLeave={() => setActiveFeature(null)}
              >
                <div className={styles.featureIcon}>
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className={styles.featureBenefits}>
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className={styles.benefit}>
                      <i className="fas fa-check"></i>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Analysis Types Section */}
      <section className={styles.analysisTypesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Comprehensive Analysis Types</h2>
            <p>Multi-dimensional analysis covering every critical aspect of your project proposals</p>
          </div>
          <div className={styles.analysisTypesGrid}>
            {analysisTypes.map((type, index) => (
              <div key={index} className={styles.analysisTypeCard}>
                <div className={styles.typeIcon}>
                  <i className={type.icon}></i>
                </div>
                <h3>{type.title}</h3>
                <p>{type.description}</p>
                <div className={styles.typeFeatures}>
                  {type.features.map((feature, idx) => (
                    <div key={idx} className={styles.typeFeature}>
                      <i className="fas fa-cog"></i>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <div className={styles.benefitsContent}>
            <div className={styles.benefitsText}>
              <h2>Why Choose AI Analysis?</h2>
              <div className={styles.benefitsList}>
                <div className={styles.benefitItem}>
                  <i className="fas fa-zap"></i>
                  <div>
                    <h4>Lightning Fast</h4>
                    <p>Complete proposal analysis in 5 to 10 minutes vs. hours of manual review</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-eye"></i>
                  <div>
                    <h4>No Details Missed</h4>
                    <p>AI never gets tired and catches every detail, cost, and potential risk</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-balance-scale"></i>
                  <div>
                    <h4>Unbiased Analysis</h4>
                    <p>Objective evaluation free from human bias and emotional decision-making</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-graduation-cap"></i>
                  <div>
                    <h4>Continuous Learning</h4>
                    <p>Our AI improves with every analysis, becoming smarter and more accurate</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.benefitsVisual}>
              <div className={styles.comparisonCard}>
                <h4>Manual vs. AI Analysis</h4>
                <div className={styles.comparison}>
                  <div className={styles.comparisonItem}>
                    <span className={styles.comparisonLabel}>Analysis Time</span>
                    <div className={styles.comparisonBars}>
                      <div className={styles.manualBar}>
                        <span>Manual: 4-6 hours</span>
                      </div>
                      <div className={styles.aiBar}>
                        <span>AI: 5 to 10 minutes</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2>Experience the Future of Proposal Analysis</h2>
            <p>Let AI transform how you evaluate contractors and make project decisions</p>
            <div className={styles.ctaButtons}>
              <button className={styles.primaryButton} onClick={handleCreateComparison}>
                <i className="fas fa-brain"></i>
                Start AI Analysis Now
              </button>
              <button className={styles.secondaryButton} onClick={() => navigate('/homeowner/pricing')}>
                <i className="fas fa-info-circle"></i>
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIProposalAnalysis; 